import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../config/api';
import { isDynamicSection, renderDynamicSection, loadDynamicSections } from '../utils/sectionRegistry';
import { LayoutComponent } from '../components/PageRender/sections/layout/LayoutComponent';

function resolveLinkUrl(linkValue) {
  if (!linkValue) return '';
  if (typeof linkValue === 'string') return linkValue;
  if (typeof linkValue === 'object' && linkValue.type === 'internal' && linkValue.pageId) {
    return `/pages/${linkValue.pageId}`;
  }
  return '';
}

function hasLinkUrl(linkValue) {
  if (!linkValue) return false;
  if (typeof linkValue === 'string') return linkValue !== '' && linkValue !== '#';
  if (typeof linkValue === 'object' && linkValue.type === 'internal') return !!linkValue.pageId;
  return false;
}

export default function PublicPage({ embed = false }) {
  const { id } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(`/pages/public/${id}`);

      if (!response.ok) {
        if (response.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
        return;
      }

      const pageData = await response.json();
      setPage(pageData);
    } catch (error) {
      console.error('Error fetching page:', error);
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Load dynamic sections first, then fetch page
    const initializePage = async () => {
      try {
        // Load dynamic sections before fetching page data
        await loadDynamicSections();
      } catch (error) {
        console.error('Failed to load dynamic sections:', error);
      }
      
      // Now fetch the page - this ensures dynamic sections are available when rendering
      await fetchPage();
    };

    initializePage();
  }, [id, fetchPage]);

  // Handle loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading page...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-red-500">
            <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Page Not Found</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  // Handle no page data
  if (!page) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500">No page data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{
      backgroundColor: page.content?.page_background_color || '#f9fafb'
    }}>
      {/* Custom CSS */}
      {page.custom_css && (
        <style dangerouslySetInnerHTML={{ __html: page.custom_css }} />
      )}

      {/* SEO Meta Tags - only effective if this is server-side rendered */}
      {page.meta_title && (
        <title>{page.meta_title}</title>
      )}

      {/* Page Content - No default padding/margin */}
      <div>
        {/* Render Page Sections */}
        {page.sections && page.sections.length > 0 ? (
          <div>
            {page.sections
              .sort((a, b) => a.order_index - b.order_index)
              .map((section, index) => (
                <PageSectionRenderer
                  key={section.id}
                  section={section}
                  pageConfig={page.content || {}}
                  isLast={index === page.sections.length - 1}
                />
              ))}
          </div>
        ) : (
          // Fallback content if no sections
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center max-w-md mx-auto px-4">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {page.title}
              </h1>
              {page.description && (
                <p className="text-xl text-gray-600">
                  {page.description}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Render Attached Forms */}
        {page.attached_forms && page.attached_forms.length > 0 && (
          <div>
            {page.attached_forms.map((attachment) => (
              <div key={attachment.id} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {attachment.form.title}
                  </h2>
                  {attachment.form.description && (
                    <p className="text-gray-600 mb-6">
                      {attachment.form.description}
                    </p>
                  )}

                  {/* Form would be rendered here - for now show a placeholder */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <p className="text-gray-500">
                      Form: {attachment.form.title}
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      Form rendering integration pending
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Component to render individual page sections
function PageSectionRenderer({ section, pageConfig, isLast }) {
  // Determine if content should be constrained or full-width
  const getContentContainerClass = () => {
    const width = pageConfig.section_width || 'boxed';

    switch (width) {
      case 'full-width':
        return 'px-4 sm:px-6 lg:px-8'; // Add padding to prevent edge touching
      case 'custom':
        return 'mx-auto px-4 sm:px-6 lg:px-8'; // Center with custom width
      case 'boxed':
      default:
        return 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8';
    }
  };

  const getContentContainerStyle = () => {
    const width = pageConfig.section_width || 'boxed';
    const customWidth = pageConfig.custom_section_width || '800px';

    if (width === 'custom') {
      return { maxWidth: customWidth };
    } else if (width === 'full-width') {
      return {}; // No constraints for full-width
    }
    return {};
  };

  const renderSectionContent = () => {
    const { section_type, content } = section;

    switch (section_type) {
      case 'header':
        return (
          <div style={{ textAlign: content.textAlign || 'center' }}>
            <h1
              className="font-bold"
              style={{
                fontSize: `${content.fontSize || 48}px`,
                fontWeight: content.fontWeight || 'bold',
                color: content.color || '#1f2937',
                textAlign: 'inherit',
                margin: 0, // Remove default margins - spacing controlled by section container
              }}
            >
              {content.text || 'Welcome!'}
            </h1>
          </div>
        );

      case 'paragraph':
        return (
          <div style={{ textAlign: content.textAlign || 'center' }}>
            <p
              style={{
                fontSize: `${content.fontSize || 18}px`,
                fontWeight: content.fontWeight || 'normal',
                color: content.color || '#6b7280',
                textAlign: 'inherit',
                margin: 0, // Remove default margins
              }}
            >
              {content.text || 'Your content goes here.'}
            </p>
          </div>
        );

      case 'image':
        return content.src ? (
          <div style={{ textAlign: content.textAlign || 'center' }}>
            <img
              src={content.src}
              alt={content.alt || ''}
              className="mx-auto"
              style={{
                width: content.width !== 'auto' ? `${content.width}px` : 'auto',
                height: content.height !== 'auto' ? `${content.height}px` : 'auto',
                borderRadius: `${content.borderRadius || 8}px`,
                maxWidth: '100%',
              }}
            />
          </div>
        ) : null;

      case 'video':
        if (!content.src) return null;

        // Format dimension with unit support
        const formatVideoDimension = (dimensionObj) => {
          if (!dimensionObj) return 'auto';
          if (dimensionObj.value === 'none' || dimensionObj.value === 'auto') return dimensionObj.value;
          return `${dimensionObj.value}${dimensionObj.unit}`;
        };

        const videoWidth = content.containerWidth
          ? formatVideoDimension(content.containerWidth)
          : '100%';
        const videoMaxWidth = content.maxWidth
          ? formatVideoDimension(content.maxWidth)
          : 'none';
        const aspectRatio = content.aspectRatio || '16:9';

        // Aspect ratio padding-bottom percentages
        const aspectRatios = {
          '16:9': '56.25%',
          '4:3': '75%',
          '1:1': '100%',
          '9:16': '177.78%',
          '21:9': '42.86%'
        };

        const isNoneAspect = aspectRatio === 'none';
        const isCustomAspect = aspectRatio === 'custom';

        return (
          <div style={{
            textAlign: content.textAlign || 'center',
            width: '100%'
          }}>
            {isNoneAspect ? (
              // None - use video's natural aspect ratio (width: 100%, height: auto)
              <div style={{
                width: videoWidth,
                maxWidth: videoMaxWidth,
                margin: '0 auto'
              }}>
                <video
                  src={content.src}
                  poster={content.posterUrl}
                  controls={content.controls !== false}
                  autoPlay={content.autoplay === true}
                  loop={content.loop === true}
                  muted={content.muted === true}
                  playsInline={content.playsinline !== false}
                  preload={content.preload || 'metadata'}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    maxWidth: '100%',
                    objectFit: content.objectFit || 'contain',
                    borderRadius: `${content.borderRadius || 8}px`,
                    backgroundColor: content.backgroundColor || '#000000',
                    opacity: content.opacity ? content.opacity / 100 : 1
                  }}
                >
                  <source src={content.src} type={content.type || 'video/mp4'} />
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : isCustomAspect ? (
              // Custom - fixed width and height
              <video
                src={content.src}
                poster={content.posterUrl}
                controls={content.controls !== false}
                autoPlay={content.autoplay === true}
                loop={content.loop === true}
                muted={content.muted === true}
                playsInline={content.playsinline !== false}
                preload={content.preload || 'metadata'}
                style={{
                  display: 'block',
                  margin: '0 auto',
                  width: videoWidth,
                  maxWidth: videoMaxWidth,
                  height: 'auto',
                  objectFit: content.objectFit || 'contain',
                  borderRadius: `${content.borderRadius || 8}px`,
                  backgroundColor: content.backgroundColor || '#000000',
                  opacity: content.opacity ? content.opacity / 100 : 1
                }}
              >
                <source src={content.src} type={content.type || 'video/mp4'} />
                Your browser does not support the video tag.
              </video>
            ) : (
              // Aspect ratio mode - responsive with padding-bottom technique
              // For videos with controls, use a wrapper to prevent cutoff
              <div style={{
                width: videoWidth,
                maxWidth: videoMaxWidth,
                margin: '0 auto'
              }}>
                <div style={{
                  position: 'relative',
                  paddingBottom: aspectRatios[aspectRatio] || '56.25%',
                  height: 0,
                  borderRadius: `${content.borderRadius || 8}px`,
                  backgroundColor: content.backgroundColor || '#000000'
                }}>
                  <video
                    src={content.src}
                    poster={content.posterUrl}
                    controls={content.controls !== false}
                    autoPlay={content.autoplay === true}
                    loop={content.loop === true}
                    muted={content.muted === true}
                    playsInline={content.playsinline !== false}
                    preload={content.preload || 'metadata'}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: content.objectFit || 'contain',
                      borderRadius: `${content.borderRadius || 8}px`,
                      opacity: content.opacity ? content.opacity / 100 : 1
                    }}
                  >
                    <source src={content.src} type={content.type || 'video/mp4'} />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            )}
          </div>
        );

      case 'cta':
        return content.text ? (
          <div style={{ textAlign: content.textAlign || 'center' }}>
            {hasLinkUrl(content.url) ? (
              <a
                href={resolveLinkUrl(content.url)}
                className="inline-block font-medium rounded-lg hover:opacity-90 transition-opacity"
                style={{
                  backgroundColor: content.backgroundColor || '#4f46e5',
                  color: content.textColor || '#ffffff',
                  padding: content.padding || '12px 24px',
                  fontSize: `${content.fontSize || 16}px`,
                  borderRadius: `${content.borderRadius || 8}px`,
                  textDecoration: 'none',
                }}
              >
                {content.text}
              </a>
            ) : (
              <button
                className="inline-block font-medium rounded-lg hover:opacity-90 transition-opacity"
                style={{
                  backgroundColor: content.backgroundColor || '#4f46e5',
                  color: content.textColor || '#ffffff',
                  padding: content.padding || '12px 24px',
                  fontSize: `${content.fontSize || 16}px`,
                  borderRadius: `${content.borderRadius || 8}px`,
                }}
              >
                {content.text}
              </button>
            )}
          </div>
        ) : null;

      case 'layout-1':
        return <LayoutComponent content={content} sectionId={section.id} />;

      default:
        // Check if this is a dynamic section
        if (isDynamicSection(section_type)) {
          // Helper function to create comprehensive spacing styles (matching ThankYou.js)
          const getSpacingStyles = (baseStyles = {}) => ({
            ...baseStyles,
            marginTop: `${content.marginTop || 0}px`,
            marginBottom: `${content.marginBottom || baseStyles.marginBottom || 24}px`,
            paddingTop: `${content.paddingTop || 0}px`,
            paddingBottom: `${content.paddingBottom || 0}px`,
            paddingLeft: `${content.paddingLeft || 0}px`,
            paddingRight: `${content.paddingRight || 0}px`,
          });

          return renderDynamicSection(section_type, content, getSpacingStyles);
        }
        
        return (
          <div className="py-8 text-center">
            <p className="text-gray-500">
              Unknown section type: {section_type}
            </p>
          </div>
        );
    }
  };

  // Apply section-level spacing styles
  const getSectionSpacing = () => {
    const { content } = section;
    return {
      marginTop: `${content.marginTop || 0}px`,
      marginBottom: `${content.marginBottom || 24}px`,
      paddingTop: `${content.paddingTop || 0}px`,
      paddingBottom: `${content.paddingBottom || 0}px`,
      paddingLeft: `${content.paddingLeft || 0}px`,
      paddingRight: `${content.paddingRight || 0}px`,
    };
  };

  // Render the section content first to check if it's null
  const sectionContent = renderSectionContent();
  
  // If section returns null (e.g., empty custom HTML section), don't render wrapper
  if (sectionContent === null) {
    return null;
  }

  // For layout sections, don't add extra wrappers - LayoutComponent handles all styling
  if (section.section_type === 'layout-1') {
    return renderSectionContent();
  }

  // For other sections, restructure to allow full-width backgrounds
  return (
    <div 
      style={{
        // Background extends full-width regardless of content width
        backgroundColor: section.content?.sectionBackgroundColor === 'transparent' 
          ? 'transparent' 
          : section.content?.sectionBackgroundColor || 'transparent',
        ...getSectionSpacing(),
      }}
    >
      {/* Content container - can be constrained or full-width based on page settings */}
      <div 
        className={getContentContainerClass()} 
        style={getContentContainerStyle()}
      >
        {sectionContent}
      </div>
    </div>
  );
}